import type { ComputedRef, Ref } from "vue";
import { useCheckout } from "@donoor/checkout";
import { capturerCommandePaypal } from "~/utils/capturePaypal";
import { suiteIntegration } from "~/utils/embed";
import { membershipsApi } from "~/services/api/memberships";
import type { CreateMembershipOrderPayload } from "~/types/membership";

export interface PendingMembershipStripeOrder {
  order_id: string;
  payment_intent_id: string;
  client_secret: string;
  stripe_account_id?: string;
}

export interface UseMembershipCheckoutProvidersDeps {
  slug: Ref<string> | ComputedRef<string>;
  /** Plan courant — utilisé pour l'organization_id de la capture PayPal. */
  plan: ComputedRef<{ organization_id?: number | string } | null>;
  stripeAccountId: ComputedRef<string | null>;
  paymentProviders: ComputedRef<{
    paypal?: { merchant_id?: string | null } | null;
  }>;
  isPaypalOnlyMode: ComputedRef<boolean>;
  usesPaypalWallets: ComputedRef<boolean>;
  selectedProvider: Ref<"stripe" | "paypal">;
  includeFees: Ref<boolean>;
  calculations: {
    amountWithFees: ComputedRef<number>;
    finalAmount: ComputedRef<number>;
  };
  buyerInfo: Ref<{
    firstName: string;
    lastName: string;
    email: string;
    confirmEmail: string;
    phone: string;
  }>;
  buildOrderPayload: () => CreateMembershipOrderPayload;
}

/**
 * useMembershipCheckoutProviders — instances de paiement (Stripe + PayPal)
 * de la page /a/[slug]. Extrait 1:1 de `pages/a/[slug]/index.vue` (Zone A
 * split >800 lignes).
 *
 * Provider primaire (Karim 2026-06-21) : aligné /c /donate /pay.
 * - PayPal-only OU (both + preferred=paypal) → CardFields PayPal + wallets PayPal
 * - sinon → Stripe Elements + wallets Stripe
 * + Bouton PayPal alternative si PayPal aussi configuré.
 */
export function useMembershipCheckoutProviders(deps: UseMembershipCheckoutProvidersDeps) {
  const {
    slug,
    plan,
    stripeAccountId,
    paymentProviders,
    isPaypalOnlyMode,
    usesPaypalWallets,
    selectedProvider,
    includeFees,
    calculations,
    buyerInfo,
    buildOrderPayload,
  } = deps;
  const config = useRuntimeConfig();
  // Langue de la page : champs carte et messages Stripe/PayPal traduits.
  const { localeIntl } = useFormatLangue();
  const localePath = useLocalePath();
  // Widget chez l'association (`?embed=1&host=…`) : la page de succès le reste.
  const route = useRoute();

  let pendingOrderResult: PendingMembershipStripeOrder | null = null;

  // Order pending PayPal (rempli quand createOrder backend a renvoyé un
  // paypal_order_id, comme pour Stripe avec client_secret).
  let pendingPaypalOrder: {
    order_id: string;
    paypal_order_id: string;
    paypal_merchant_id?: string;
  } | null = null;

  /** Utilisé par useMembershipOrderSubmit pour mémoriser l'order créé au submit. */
  function setPendingStripeOrder(r: PendingMembershipStripeOrder): void {
    pendingOrderResult = r;
  }

  const checkout = useCheckout({
    provider: "stripe",
    locale: localeIntl.value,
    publicKey: config.public.stripePublishableKey as string,
    // Getter (PAS `.value` figé) : résolu à l'init du provider, sinon Stripe.js
    // s'initialise sur la plateforme → « No such payment_intent ».
    connectedAccountId: () => stripeAccountId.value || undefined,
    currency: "eur",
    // Montant DÉBITÉ, quelle que soit la case frais : `amountWithFees` vaut
    // adhésion + don + soutien quand les frais ne sont pas couverts. Avec
    // `finalAmount`, Apple Pay / Google Pay annonçaient l'adhésion seule.
    amount: computed(() => calculations.amountWithFees.value),
    donor: computed(() => ({
      email: buyerInfo.value.email,
      name: `${buyerInfo.value.firstName} ${buyerInfo.value.lastName}`.trim(),
      phone: buyerInfo.value.phone,
    })),
    recurring: ref(false),
    feesCovered: includeFees,
    createPaymentIntent: async () => {
      if (pendingOrderResult?.client_secret) {
        return { clientSecret: pendingOrderResult.client_secret, transactionId: pendingOrderResult.payment_intent_id };
      }
      // ⚠️ Force payment_provider=stripe pour ce path Stripe (le ref
      // selectedProvider est toujours rendu cohérent en amont mais on est
      // explicite ici pour ne PAS dépendre d'un état transitoire).
      selectedProvider.value = "stripe";
      const payload = buildOrderPayload();
      const orderResult = await membershipsApi.createOrder(slug.value, payload);
      if (orderResult.free_with_promo) throw new Error("Order is free with promo, should not reach Stripe");
      if (orderResult.status === "pending_approval") throw new Error("Order requires approval, should not reach Stripe");
      pendingOrderResult = {
        order_id: orderResult.order_id,
        payment_intent_id: orderResult.payment_intent_id ?? "",
        client_secret: orderResult.client_secret ?? "",
        stripe_account_id: orderResult.stripe_account_id,
      };
      if (!pendingOrderResult.client_secret) throw new Error("Order créé sans client_secret");
      return { clientSecret: pendingOrderResult.client_secret, transactionId: pendingOrderResult.payment_intent_id };
    },
    successUrl: () => {
      const orderQuery = pendingOrderResult?.order_id ? `&order_id=${pendingOrderResult.order_id}` : "";
      return `${localePath({ path: `/a/${slug.value}/success` })}?email=${encodeURIComponent(buyerInfo.value.email || "")}${orderQuery}${suiteIntegration(route.query)}`;
    },
  });

  // Instance PayPal — utilisée pour CardFields PayPal (preferred=paypal) ET
  // pour les wallets/Smart Button. `createPaymentIntent` ici crée l'order
  // membership avec payment_provider="paypal" et retourne le paypal_order_id
  // que le SDK PayPal utilise comme orderID.
  const paypalCheckout = useCheckout({
    provider: "paypal",
    locale: localeIntl.value,
    publicKey: (config.public.paypalClientId as string) || "",
    // Getter (PAS `.value` figé) : résolu à l'init du provider.
    connectedAccountId: () =>
      paymentProviders.value.paypal?.merchant_id || undefined,
    currency: "eur",
    // Montant DÉBITÉ (cf. instance Stripe ci-dessus).
    amount: computed(() => calculations.amountWithFees.value),
    donor: computed(() => ({
      email: buyerInfo.value.email,
      name: `${buyerInfo.value.firstName} ${buyerInfo.value.lastName}`.trim(),
      phone: buyerInfo.value.phone,
    })),
    recurring: ref(false),
    feesCovered: includeFees,
    partnerAttributionId: (config.public.paypalPartnerAttributionId as string) || "DONOOR_SP_PPCP",
    // buyer-country réservé au sandbox (refusé en prod → 400 SDK).
    sandbox: (config.public.mode as string) !== "prod",
    pageType: "checkout",
    createPaymentIntent: async () => {
      if (pendingPaypalOrder?.paypal_order_id) {
        return {
          clientSecret: pendingPaypalOrder.paypal_order_id,
          transactionId: pendingPaypalOrder.paypal_order_id,
        };
      }
      selectedProvider.value = "paypal";
      const payload = buildOrderPayload();
      const orderResult = await membershipsApi.createOrder(slug.value, payload);
      if (orderResult.free_with_promo) throw new Error("Order is free with promo, should not reach PayPal");
      if (orderResult.status === "pending_approval") throw new Error("Order requires approval, should not reach PayPal");
      if (!orderResult.paypal_order_id) throw new Error("Order créé sans paypal_order_id");
      pendingPaypalOrder = {
        order_id: orderResult.order_id,
        paypal_order_id: orderResult.paypal_order_id,
        paypal_merchant_id: orderResult.paypal_merchant_id,
      };
      return {
        clientSecret: pendingPaypalOrder.paypal_order_id,
        transactionId: pendingPaypalOrder.paypal_order_id,
      };
    },
    successUrl: () => {
      const orderQuery = pendingPaypalOrder?.order_id
        ? `&order_id=${pendingPaypalOrder.order_id}`
        : "";
      return `${localePath({ path: `/a/${slug.value}/success` })}?email=${encodeURIComponent(buyerInfo.value.email || "")}${orderQuery}${suiteIntegration(route.query)}`;
    },
    // Capture serveur-side générique PayPal (orderId quelconque — backend
    // retrouve le merchant_id depuis l'org du transaction record).
    // Lève une erreur si PayPal refuse (carte refusée…) : jamais de « merci »
    // sans paiement réel (cf. utils/capturePaypal.ts).
    onCapture: (orderId) => {
      const orgId =
        typeof plan.value?.organization_id === "number"
          ? plan.value.organization_id
          : Number(plan.value?.organization_id);
      return capturerCommandePaypal(config.public.apiBaseUrl as string, orderId, orgId);
    },
  });

  // Composable de mount unifié — route Stripe Express OU PayPal Apple+Google
  // selon preferred (Karim 2026-06-21).
  const { mountPrimaryWallets } = useWalletsMount({
    stripeCheckout: checkout,
    paypalCheckout,
    isPaypalOnlyMode,
    usesPaypalWallets,
  });

  return {
    checkout,
    paypalCheckout,
    mountPrimaryWallets,
    setPendingStripeOrder,
  };
}
